<template>
  <div class="individual_analytics h-1/4">    
    <!-- 無數據狀態 -->
    <div v-if="!sortedTableData.length" class="flex flex-col items-center justify-center py-12">
      <div class="w-16 h-16 mb-4 opacity-50">
        <!-- 空狀態圖標 -->
        <svg class="w-full h-full" fill="currentColor" viewBox="0 0 20 20">
          <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/>
        </svg>
      </div>
      <div class="text-gray-500">{{ $t('public_profiles.collection_empty.post') }}</div>
    </div>

    <!-- 表格內容 -->
    <template v-else>
      <!-- 表格標題 -->
      <div 
        ref="tableHeader"
        class="individual_title flex pb-2 sm:pb-0 pt-4
               sm:grid sm:grid-cols-[7fr_4fr_4fr_4fr_4fr_4fr_4fr] 
               text-grey-3 text-12px font-[600] overflow-auto overflow-x-scroll sm:overflow-x-hidden
               fixed-section sm:sticky sm:top-[407px] lg:top-[391px] z-20 bg-bg-base"
        @scroll="syncHeaderScroll"
      >
        <div class='h-5 w-40 flex-shrink-0 sm:w-auto sm:mr-0'>
          {{ $t('private_profiles.analytic.model_title') }}
        </div>
        
        <div 
          v-for="header in tableHeaders"
          :key="header.key"
          :class="header.class"
          class="cursor-pointer hover:text-white transition-colors"
          @click="handleSort(header.key)"
        >
          {{ $t(header.title) }}
          <span 
            class='sort-triangle'
            :class="getSortClass(header.key)"
          ></span>
        </div>
        
        <hr class='col-span-12 border-gray-500 my-2'>
      </div>

      <!-- 表格主體 -->
      <div 
        ref="tableBody"
        class="individual-posts-container flex flex-col pr-1 -mr-3
               overflow-x-scroll sm:overflow-x-hidden scrollbar"
        @scroll="syncBodyScroll"
      >
        <analytics-item 
          v-for="(post, index) in sortedTableData"
          :key="post.post_id"
          :post="post"
          :index="index"
          :is-expanded="isPostExpanded(post.post_id)"
          :revenue-details="getPostRevenueDetails(post.post_id)"
          :loading-revenue="loadingRevenue[post.post_id]"
          @toggle-revenue="handleToggleRevenue"
        />
      </div>
    </template>
  </div>
</template>

<script>
import { ref, inject, computed } from 'vue'
import AnalyticsItem from './AnalyticsItem.vue'

export default {
  name: 'ProfileAnalyticsTable',
  components: {
    AnalyticsItem
  },
  setup() {
    // 從父組件注入 analytics 實例
    const analytics = inject('analytics')
    
    if (!analytics) {
      console.error('ProfileAnalyticsTable: analytics not provided')
      return {}
    }
    
    const {
      getSortedTableData,
      getSortClass,
      sortPostsBy,
      isPostExpanded,
      getPostRevenueDetails,
      togglePostRevenue
    } = analytics

    // DOM 引用
    const tableHeader = ref(null)
    const tableBody = ref(null)
    
    // 本地狀態
    const loadingRevenue = ref({})

    // 表格標題配置
    const tableHeaders = [
      { key: 'revenues', title: 'private_profiles.analytic.revenues', class: 'h-5 w-24 flex-shrink-0 sm:w-auto' },
      { key: 'likes', title: 'private_profiles.analytic.likes', class: 'h-5 w-20 flex-shrink-0 sm:w-auto' },
      { key: 'views', title: 'private_profiles.analytic.views', class: 'h-5 w-20 flex-shrink-0 sm:w-auto' },
      { key: 'download', title: 'private_profiles.analytic.download', class: 'h-5 w-24 flex-shrink-0 sm:w-auto' },
      { key: 'saved', title: 'private_profiles.analytic.saved', class: 'h-5 w-20 flex-shrink-0 sm:w-auto' },
      { key: 'rating', title: 'private_profiles.analytic.rating', class: 'h-5 w-20 flex-shrink-0 sm:w-auto' }
    ]

    // 計算屬性：獲取排序後的表格數據
    const sortedTableData = computed(() => {
      return getSortedTableData()
    })

    // 處理排序
    const handleSort = (sortKey) => {
      sortPostsBy(sortKey)
    }

    // 處理展開收起
    const handleToggleRevenue = async (postId) => {
      loadingRevenue.value[postId] = true
      
      try {
        await togglePostRevenue(postId)
      } catch (error) {
        console.error('Error toggling revenue:', error)
      } finally {
        loadingRevenue.value[postId] = false
      }
    }

    // 滾動同步
    const syncHeaderScroll = () => {
      if (tableBody.value && tableHeader.value) {
        tableBody.value.scrollLeft = tableHeader.value.scrollLeft
      }
    }

    const syncBodyScroll = () => {
      if (tableHeader.value && tableBody.value) {
        tableHeader.value.scrollLeft = tableBody.value.scrollLeft
      }
    }

    return {
      tableHeader,
      tableBody,
      tableHeaders,
      sortedTableData,
      loadingRevenue,
      handleSort,
      handleToggleRevenue,
      syncHeaderScroll,
      syncBodyScroll,
      getSortClass,
      isPostExpanded,
      getPostRevenueDetails
    }
  }
}
</script>
